import React, { useState, useEffect, useRef, useMemo } from 'react';

// --- ICONOS SVG PREMIUM ---
const IconChevronDown = ({ className = "w-4 h-4" }) => (
    <svg className={`${className} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const IconShield = () => (
    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

const IconUsers = () => (
    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);

// --- COMPONENTE AUXILIAR INTERNO: CHECKLIST MULTI-FILTRO CON DESPLIEGUE INLINE ---
const MultiSelectDropdown = ({ label, options, selectedValues, onChange, isOpen, onToggle }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Cerrar al hacer click fuera del dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                if (isOpen) onToggle(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onToggle]);

    // Limpia la búsqueda automáticamente cuando se cierra el menú desplegable
    useEffect(() => {
        if (!isOpen) setSearchQuery('');
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        return options.filter(opt => String(opt || '').toLowerCase().includes(searchQuery.toLowerCase()));
    }, [options, searchQuery]);

    const handleToggle = (value) => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter(v => v !== value));
        } else {
            onChange([...selectedValues, value]);
        }
    };

    const hasSelection = selectedValues.length > 0;

    return (
        <div className="text-left w-full" ref={dropdownRef}>
            <div className="flex items-center justify-between mb-1">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{label}</label>
                {hasSelection && (
                    <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-[#01032d] font-bold rounded-md">
                        {selectedValues.length} sel.
                    </span>
                )}
            </div>
            <button
                type="button"
                onClick={() => onToggle(!isOpen)}
                className={`w-full border rounded-xl p-2.5 text-xs font-bold text-left flex justify-between items-center transition-all outline-none ${
                    hasSelection 
                        ? 'border-blue-900 bg-blue-50/20 text-[#01032d]' 
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                }`}
            >
                <span className="truncate max-w-[85%]">
                    {!hasSelection ? 'TODOS' : selectedValues.join(', ')}
                </span>
                <IconChevronDown className={`w-3 h-3 text-slate-400 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* DESPLIEGUE INLINE (Acordeón que empuja el contenido hacia abajo de manera natural) */}
            {isOpen && (
                <div className="w-full mt-1.5 bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 max-h-56 overflow-hidden flex flex-col animate-fade-in">
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-xl p-2 text-[11px] mb-2 font-medium outline-none focus:border-blue-900 transition-colors"
                    />
                    <div className="flex justify-between mb-2 px-1 text-[10px] font-black uppercase text-[#01032d]">
                        <button type="button" onClick={() => onChange([])} className="hover:underline">Limpiar</button>
                        <button type="button" onClick={() => onChange(options)} className="hover:underline">Todos</button>
                    </div>
                    <div className="overflow-y-auto space-y-1 flex-grow pr-1 custom-scrollbar">
                        {filteredOptions.length === 0 ? (
                            <p className="text-[11px] text-slate-400 italic text-center py-2">Sin coincidencias</p>
                        ) : (
                            filteredOptions.map(opt => {
                                const isChecked = selectedValues.includes(opt);
                                return (
                                    <label 
                                        key={opt} 
                                        className={`flex items-center gap-2.5 p-1.5 px-2 hover:bg-white rounded-lg cursor-pointer text-xs font-semibold transition-colors ${
                                            isChecked ? 'bg-blue-50/30 text-[#01032d]' : 'text-slate-600'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleToggle(opt)}
                                            className="w-4 h-4 rounded text-[#01032d] border-slate-300 focus:ring-slate-500 cursor-pointer"
                                        />
                                        <span className="truncate">{opt}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * FiltrosContratosSidebar - Panel lateral exclusivo de control para la sección de Contratos.
 */
const FiltrosContratosSidebar = ({
    isDbLoaded,
    opcionesFiltrosUnicas = {},
    multiFiltros = {},
    setMultiFiltros,
    agruparPor,
    setAgruparPor
}) => {
    // Control de acordeones para las secciones de segmentación
    const [seccionesAbiertas, setSeccionesAbiertas] = useState({
        riesgo: true,
        comercial: false
    });

    // Control unificado de dropdown activo para evitar múltiples aperturas simultáneas
    const [activeDropdown, setActiveDropdown] = useState(null);

    const toggleSeccion = (seccion) => {
        setSeccionesAbiertas(prev => ({ ...prev, [seccion]: !prev[seccion] }));
    };

    // Desestructuración segura del hook de contratos con fallbacks
    const {
        estadoPoliza = [],
        tipoOperacion = [],
        subOperacion = [],
        compania = [],
        vendedor = [],
        suscriptor = [],
        canal = [],
        canalNombre = [],
        subcanalNombre = []
    } = opcionesFiltrosUnicas || {};

    // Indicadores numéricos de filtros activos en cada categoría
    const contadorRiesgo = useMemo(() => {
        return (multiFiltros.estadoPoliza?.length || 0) +
               (multiFiltros.tipoOperacion?.length || 0) +
               (multiFiltros.subOperacion?.length || 0) +
               (multiFiltros.compania?.length || 0);
    }, [multiFiltros]);

    const contadorComercial = useMemo(() => {
        return (multiFiltros.vendedor?.length || 0) +
               (multiFiltros.suscriptor?.length || 0) +
               (multiFiltros.canal?.length || 0) +
               (multiFiltros.canalNombre?.length || 0) +
               (multiFiltros.subcanalNombre?.length || 0);
    }, [multiFiltros]);

    return (
        <aside className="w-80 shrink-0 bg-white border-r border-slate-200/80 overflow-y-auto p-5 flex flex-col gap-6 custom-scrollbar text-left h-full shadow-[2px_0_10px_-5px_rgba(0,0,0,0.02)]">
            
            {/* 1. INFORMACIÓN ESTÁTICA DEL MÓDULO */}
            <div className="space-y-2 text-left">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    1. Origen de Datos
                </label>
                <div className="p-3 bg-gradient-to-r from-indigo-900 to-indigo-950 text-white rounded-xl border border-indigo-900 shadow-sm flex items-center gap-3 select-none">
                    <span className="text-[16px] bg-white/10 p-1.5 rounded-lg">📜</span>
                    <div>
                        <h4 className="text-[11px] font-black uppercase tracking-wider leading-none">Contratos</h4>
                        <p className="text-[9px] text-indigo-300 font-bold mt-1">BI & Analítica de Cuentas</p>
                    </div>
                </div>
            </div>

            {/* 2. CRITERIO DE AGRUPAMIENTO GENERAL */}
            {isDbLoaded && (
                <div className="space-y-2.5 text-left pt-2 border-t border-slate-100">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                        2. Agrupar Previsualización
                    </label>
                    <select 
                        value={agruparPor} 
                        onChange={(e) => setAgruparPor(e.target.value)} 
                        className="w-full border border-slate-200 rounded-xl p-2.5 text-xs font-bold bg-white text-[#01032d] outline-none cursor-pointer transition-all hover:border-slate-300"
                    >
                        <option value="NINGUNO">LISTADO PLANO</option>
                        <option value="estadoPoliza">ESTADO EMISIÓN</option>
                        <option value="tipoOperacion">TIPO DE OPERACIÓN</option>
                        <option value="compania">COMPAÑÍA</option>
                        <option value="vendedor">VENDEDOR / RESPONSABLE</option>
                    </select>
                </div>
            )}

            {/* 3. SEGMENTACIÓN DE MULTI-FILTROS */}
            {isDbLoaded ? (
                <div className="space-y-4 text-left pt-2 border-t border-slate-100 animate-fade-in relative">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block pb-1">
                        3. Filtros Secundarios
                    </label>

                    {/* SECCIÓN A: ESTRUCTURA DE RIESGO */}
                    <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                        <button
                            type="button"
                            onClick={() => toggleSeccion('riesgo')}
                            className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                                seccionesAbiertas.riesgo ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <IconShield />
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Estructura del Riesgo</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {contadorRiesgo > 0 && (
                                    <span className="text-[9px] px-2 py-0.5 bg-indigo-100 text-indigo-800 font-extrabold rounded-full">
                                        {contadorRiesgo}
                                    </span>
                                )}
                                <IconChevronDown className={`w-3.5 h-3.5 text-slate-400 ${seccionesAbiertas.riesgo ? 'rotate-180' : ''}`} />
                            </div>
                        </button>
                        
                        {seccionesAbiertas.riesgo && (
                            <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                                <MultiSelectDropdown 
                                    label="Estado de Emisión" 
                                    options={estadoPoliza} 
                                    selectedValues={multiFiltros.estadoPoliza || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, estadoPoliza: vals }))} 
                                    isOpen={activeDropdown === 'estadoPoliza'}
                                    onToggle={(open) => setActiveDropdown(open ? 'estadoPoliza' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Tipo de Operación" 
                                    options={tipoOperacion} 
                                    selectedValues={multiFiltros.tipoOperacion || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, tipoOperacion: vals }))} 
                                    isOpen={activeDropdown === 'tipoOperacion'}
                                    onToggle={(open) => setActiveDropdown(open ? 'tipoOperacion' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Sub Operación" 
                                    options={subOperacion} 
                                    selectedValues={multiFiltros.subOperacion || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, subOperacion: vals }))} 
                                    isOpen={activeDropdown === 'subOperacion'}
                                    onToggle={(open) => setActiveDropdown(open ? 'subOperacion' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Compañía Aseguradora" 
                                    options={compania} 
                                    selectedValues={multiFiltros.compania || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, compania: vals }))} 
                                    isOpen={activeDropdown === 'compania'}
                                    onToggle={(open) => setActiveDropdown(open ? 'compania' : null)}
                                />
                            </div>
                        )}
                    </div>

                    {/* SECCIÓN B: ESTRUCTURA COMERCIAL */}
                    <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                        <button
                            type="button"
                            onClick={() => toggleSeccion('comercial')}
                            className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                                seccionesAbiertas.comercial ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <IconUsers />
                                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Estructura Comercial</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {contadorComercial > 0 && (
                                    <span className="text-[9px] px-2 py-0.5 bg-emerald-100 text-emerald-800 font-extrabold rounded-full">
                                        {contadorComercial}
                                    </span>
                                )}
                                <IconChevronDown className={`w-3.5 h-3.5 text-slate-400 ${seccionesAbiertas.comercial ? 'rotate-180' : ''}`} />
                            </div>
                        </button>
                        
                        {seccionesAbiertas.comercial && (
                            <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                                <MultiSelectDropdown 
                                    label="Vendedor" 
                                    options={vendedor} 
                                    selectedValues={multiFiltros.vendedor || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, vendedor: vals }))} 
                                    isOpen={activeDropdown === 'vendedor'}
                                    onToggle={(open) => setActiveDropdown(open ? 'vendedor' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Suscriptor" 
                                    options={suscriptor} 
                                    selectedValues={multiFiltros.suscriptor || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, suscriptor: vals }))} 
                                    isOpen={activeDropdown === 'suscriptor'}
                                    onToggle={(open) => setActiveDropdown(open ? 'suscriptor' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Tipo de Canal" 
                                    options={canal} 
                                    selectedValues={multiFiltros.canal || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, canal: vals }))} 
                                    isOpen={activeDropdown === 'canal'}
                                    onToggle={(open) => setActiveDropdown(open ? 'canal' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Nombre del Canal" 
                                    options={canalNombre} 
                                    selectedValues={multiFiltros.canalNombre || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, canalNombre: vals }))} 
                                    isOpen={activeDropdown === 'canalNombre'}
                                    onToggle={(open) => setActiveDropdown(open ? 'canalNombre' : null)}
                                />
                                <MultiSelectDropdown 
                                    label="Subcanal / Asociado" 
                                    options={subcanalNombre} 
                                    selectedValues={multiFiltros.subcanalNombre || []} 
                                    onChange={(vals) => setMultiFiltros(p => ({ ...p, subcanalNombre: vals }))} 
                                    isOpen={activeDropdown === 'subcanalNombre'}
                                    onToggle={(open) => setActiveDropdown(open ? 'subcanalNombre' : null)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="text-center py-8 px-4 bg-slate-50 border border-slate-100 rounded-2xl">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Esperando datos...</p>
                    <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">Establece arriba el intervalo de consulta de contratos.</p>
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
                .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
                .animate-scale-up { animation: scaleUp 0.15s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes scaleUp { from { opacity: 0; transform: scale(0.95) translateY(-5px); } to { opacity: 1; transform: scale(1) translateY(0); } }
            `}</style>
        </aside>
    );
};

export default FiltrosContratosSidebar;